import type { HtmlAttributes } from '@studio/design-system/props';
import type { Settings } from '@studio/models/control';
import { ControlDefaults } from '@studio/models/control';
import type { Configuration } from '@studio/models/control/http';
import { HttpDefaults } from '@studio/models/control/http';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn, useMount } from '@studio/react-utils/hooks';
import type { T_Nullable } from '@tryiris-ai/js-types';
import { defaultsDeep } from 'lodash-es';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { shallow } from 'zustand/shallow';
import { useProducer, useStore } from '../store/hooks.js';
import { Http as _Http } from '#~/control/components/editor/index.js';
import { useAnalytics } from '#~/analytics/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/index.js';

/** @internal */
const Inner = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();
  const { track } = useAnalytics();

  const settings = useStore(
    ({ candidate }) =>
      // We know this is a normalized HTTP configuration.
      candidate.control as Settings<Configuration>,
    shallow
  );

  // Per ENG-3960 §E12 — canonical device cohort props read outside producer callback.
  const deviceModelId = useStore((µ) => µ.manufacturer?.config?.model?.id?.toString());
  const deviceBrandId = useStore((µ) => µ.manufacturer?.config?.model?.brandId?.toString());

  const handleChange = useLatestFn((value: Settings) => {
    producer((draft) => {
      draft.candidate.control = value;
      track(EVENT_NAMES.EDIT_ADVANCED_CONTROL_SETTINGS, EVENT_CATEGORIES.ADVANCED_SETTINGS, {
        controlSettingKey: 'http',
        controlSettingValue: value,
        ...(deviceModelId ? { device_model_id: deviceModelId } : {}),
        ...(deviceBrandId ? { device_brand_id: deviceBrandId } : {})
      });
    });
  });

  return <_Http settings={toAccessor(settings, handleChange)} {...props} />;
};

/** HTTP settings. */
export const Http = (props: HtmlAttributes): T_Nullable<ReactElement> => {
  const [isPrimed, setPrimed] = useState(false);
  const producer = useProducer();

  // On mount, normalize the Visca/IP control settings.
  useMount(() => {
    queueMicrotask(() => {
      producer(({ candidate }) => {
        const { control, video } = candidate;

        // Normalize configuration.
        candidate.control = defaultsDeep(
          { ...control },
          video && 'host' in video ? { host: video.host } : {},
          HttpDefaults,
          ControlDefaults
        ) as Settings<Configuration>;
      });

      setPrimed(true);
    });
  });

  return isPrimed ? <Inner {...props} /> : null;
};
